import { Typography } from '@breetometer/components';
import { useModalApi, useOnboardingTodoListApi } from 'BreetHooks';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import { Loader } from '../Loader';
import {
	btnTitleMap,
	type IDialogOnboardingTodoListItemStatus,
	type IDialogOnboardingTodoListSteps,
	kybStatusMap,
} from './DialogOnboardingTodoList.helpers';
import DialogOnboardingTodoListItem from './DialogOnboardingTodoList.Item';

const DialogOnboardingTodoList = () => {
	const { closeModalDirect } = useModalApi();

	const {
		kybStatus,
		isFirstProductionApiKey,
		isLoading,
		isMFAAuthentication,
		isFirstTransaction,
		isProductionWebhookUrl,
		isKybCompleted,
	} = useOnboardingTodoListApi();

	const navigate = useNavigate();

	const navigateTo = useCallback(
		(path: string) => {
			navigate(path)
				?.then(() => closeModalDirect('onBoardingTodoList'))
				.catch(console.error);
		},
		[closeModalDirect, navigate]
	);

	const handleItemBtnClick = useCallback(
		(onboardingSteps: IDialogOnboardingTodoListSteps) => () => {
			if (onboardingSteps === 'complete-kyb') {
				navigateTo('/register-business');
			}
		},
		[navigateTo]
	);

	const todoListkybStatus: IDialogOnboardingTodoListItemStatus = kybStatus ? kybStatusMap[kybStatus] : 'get-started';

	const btnTitle = kybStatus ? btnTitleMap[kybStatus] : 'get-started';

	return (
		<div className='todo-list-container bg-primary-500'>
			<Typography
				as='h4'
				className='color-neutral-50'
			>
				Just a few steps before you're Breet-ready
			</Typography>

			{isLoading ? (
				<Loader />
			) : (
				<div className='todo-list'>
					<DialogOnboardingTodoListItem
						header='Complete KYB'
						subHeader='Verify your business details to unlock full platform access.'
						status={isKybCompleted ? 'completed' : todoListkybStatus}
						btnTitle={isKybCompleted ? 'completed' : btnTitle}
						onClick={handleItemBtnClick('complete-kyb')}
					/>

					<DialogOnboardingTodoListItem
						header='Initiate Sandbox'
						subHeader='Set up your test environment to start building and experimenting.'
						status='pending'
						onClick={handleItemBtnClick('initiate-sandbox')}
					/>

					<DialogOnboardingTodoListItem
						header='First Production API Key'
						subHeader='Generate your live API key to enable real transactions.'
						status={isFirstProductionApiKey ? 'pending' : 'completed'}
						onClick={handleItemBtnClick('first-production-api-key')}
					/>

					<DialogOnboardingTodoListItem
						header='Production Webhook URL'
						subHeader='Configure your webhook to receive real-time transaction updates.'
						status={isProductionWebhookUrl ? 'completed' : 'pending'}
						onClick={handleItemBtnClick('production-webhook-url')}
					/>

					<DialogOnboardingTodoListItem
						header='Make First Transaction'
						subHeader='Make your first live transaction to get started and enjoy full benefits.'
						status={isFirstTransaction ? 'completed' : 'pending'}
						onClick={handleItemBtnClick('first-production-transaction')}
					/>

					<DialogOnboardingTodoListItem
						header='MFA Authentication'
						subHeader='For multi-layer security, enable third-party authenticator app.'
						status={isMFAAuthentication ? 'completed' : 'pending'}
						isLast
						onClick={handleItemBtnClick('mfa-authentication')}
					/>
				</div>
			)}
		</div>
	);
};

export default DialogOnboardingTodoList;
